import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

import { logEvent } from '../../utils/eventLog';
import { OPACITY } from '../../theme';
import { CARD_SX, DASH } from '../Dashboard/tokens';
import { GhostButton } from '../Dashboard/components/GhostButton';
import { BENCH } from '../Benchmarks/tokens';
import { GROWTH_ROUTE, tileCopy } from './growth';
import { fetchGrowthTile } from './growthApi';
import type { GrowthTile as Tile } from './types';

export type TileSource = () => Promise<Tile>;

const OPEN_CHIP_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  px: '8px',
  height: 20,
  borderRadius: '10px',
  bgcolor: DASH.warnChip.bg,
  color: DASH.warnChip.fg,
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 700,
} as const;

export function GrowthTile({ source = fetchGrowthTile }: { source?: TileSource } = {}) {
  const navigate = useNavigate();
  const [tile, setTile] = useState<Tile | null>(null);
  useEffect(() => {
    let active = true;
    source()
      .then((data) => {
        if (active) setTile(data);
      })
      .catch((err: unknown) => {
        if (active) setTile(null);
        console.warn('growth tile unavailable; showing the dashboard without it', err);
      });
    return () => {
      active = false;
    };
  }, [source]);
  if (!tile) return null;
  const copy = tileCopy(tile);
  const openDetails = () => {
    void logEvent('growth.tile_opened.v1').catch(() => {});
    const year = tile.period?.schoolYear ?? tile.years?.[0];
    navigate(GROWTH_ROUTE, year === undefined ? undefined : { state: { year } });
  };
  return (
    <Box
      component="section"
      role="region"
      aria-label="Flashlight360 Growth"
      data-testid="growth-tile"
      sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '20px', boxSizing: 'border-box', width: '100%' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <Typography component="h2" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: DASH.ink, m: 0 }}>
          Flashlight360 Growth
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {copy.open && (
            <Box component="span" data-testid="growth-tile-open" sx={OPEN_CHIP_SX}>
              Open
            </Box>
          )}
          <Typography component="span" sx={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: DASH.muted }}>
            {copy.periodLine}
          </Typography>
        </Box>
      </Box>
      {copy.empty ? (
        <Typography component="p" sx={{ fontSize: '24px', lineHeight: '32px', fontWeight: 700, color: DASH.muted, m: 0 }}>
          No results yet
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography
              component="span"
              data-testid="growth-tile-number"
              sx={{ fontSize: '64px', lineHeight: '64px', fontWeight: 800, letterSpacing: '-0.03em', color: DASH.ink }}
              style={{ opacity: copy.open ? OPACITY.provisional : 1 }}
            >
              {copy.headline.number}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography component="span" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: DASH.ink }}>
                {copy.headline.line}
              </Typography>
              <Typography component="span" sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
                {copy.headline.of}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {copy.skills.map((skill) => (
              <Box key={skill.label} data-testid="growth-tile-skill" sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Typography component="span" sx={{ width: 140, flexShrink: 0, fontSize: '16px', lineHeight: '24px', fontWeight: 600, color: DASH.ink }}>
                  {skill.label}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography component="span" data-testid="growth-tile-percent" sx={{ fontSize: '12px', lineHeight: '16px', fontWeight: 700, color: DASH.ink, whiteSpace: 'nowrap' }} style={{ opacity: copy.open ? OPACITY.provisional : 1 }}>
                    {`${skill.percent}% ${skill.word}`}
                  </Typography>
                  <Box sx={{ width: '100%', height: 12, borderRadius: '4px', bgcolor: BENCH.tile.track, overflow: 'hidden' }}>
                    <Box
                      data-testid="growth-tile-bar"
                      sx={{ height: '100%', bgcolor: BENCH.tile.fill }}
                      style={{ width: `${skill.percent}%`, opacity: copy.open ? OPACITY.provisional : 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
      <Box sx={{ borderTop: `1px solid ${DASH.rowBorder}`, pt: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <Typography component="span" sx={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: DASH.muted }}>
          {copy.foot}
        </Typography>
        {(!copy.empty || (tile.years?.length ?? 0) > 0) && <GhostButton label="See details" onClick={openDetails} endIcon={<ChevronRightIcon sx={{ fontSize: 20 }} />} />}
      </Box>
    </Box>
  );
}
